<script setup lang="ts">
import { computed } from 'vue'

import BetaPageOrderHandle from '@/components/beta-dashboard/atoms/BetaPageOrderHandle.vue'
import BetaPagePublicLink from '@/components/beta-dashboard/atoms/BetaPagePublicLink.vue'
import BetaPageSettingsIcon from '@/components/beta-dashboard/atoms/BetaPageSettingsIcon.vue'
import type { BetaPage } from '@/types/beta-dashboard'
import { isExternalLinkPage } from '@/utils/page'

const props = defineProps<{
    page: BetaPage
    current?: boolean
    locked?: boolean
    orderDisabled?: boolean
}>()

const emit = defineEmits<{
    select: [page: BetaPage]
    configure: [page: BetaPage]
}>()

const hint = computed(() => {
    if (props.locked) return 'Página inicial'
    if (isExternalLinkPage(props.page)) return 'Link externo'
    if (props.page.listPages == 1) return 'Listagem de publicações'
    return 'Página de conteúdo'
})
</script>

<template>
    <div class="beta-page-navigation-item">
        <BetaPageOrderHandle
            :name="page.name"
            :locked="locked"
            :disabled="orderDisabled"
            disabled-reason="Aguarde a atualização das páginas"
        />
        <button
            type="button"
            :class="['beta-nav-disclosure__item', current && 'is-current']"
            :aria-current="current ? 'page' : undefined"
            :aria-label="`${page.name}. ${hint}`"
            :title="`${page.name}. ${hint}`"
            @click="emit('select', page)"
        >
            <strong>{{ page.name }}</strong>
        </button>
        <div class="beta-page-navigation-item__actions">
            <BetaPagePublicLink :page="page" />
            <button
                type="button"
                class="beta-nav-disclosure__item-action"
                :aria-label="`Configurar ${page.name}`"
                :title="`Configurar ${page.name}`"
                @click="emit('configure', page)"
            >
                <BetaPageSettingsIcon aria-hidden="true" />
            </button>
        </div>
    </div>
</template>

<style scoped>
.beta-page-navigation-item {
    display: grid;
    min-width: 0;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    align-items: center;
    gap: 2px;
}

.beta-nav-disclosure__item {
    min-width: 0;
    border-radius: 8px;
    padding: 10px 2px;
    color: #607188;
    text-align: left;
}

.beta-nav-disclosure__item strong {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow-wrap: anywhere;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.5;
}

.beta-page-navigation-item__actions {
    display: flex;
    align-items: center;
}

.beta-nav-disclosure__item-action {
    display: grid;
    width: 28px;
    height: 32px;
    place-items: center;
    border-radius: 8px;
    color: #7b899b;
}

.beta-nav-disclosure__item-action svg {
    width: 20px;
    height: 20px;
}

.beta-nav-disclosure__item:hover,
.beta-nav-disclosure__item.is-current,
.beta-nav-disclosure__item-action:hover {
    background: color-mix(in srgb, var(--beta-accent) 9%, white);
    color: var(--beta-accent);
}

.beta-nav-disclosure__item:focus-visible,
.beta-nav-disclosure__item-action:focus-visible {
    outline: 2px solid var(--beta-accent);
    outline-offset: 1px;
}
</style>
